import { ProductType } from '@/AppGlobal';
import { ReactComponent as CheckOn } from '@/images/check_on.svg';
import { BankCardTagType, BankEddState, EddaBankInfo } from '@/interface/KKC/edda';
import { handleEnClsName } from '@/utils';
import classNames from 'classnames';
import { t } from 'i18next';
import { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import VCreditCheckbox from '../VCreditCheckbox';
import { BankInfoCardStatus } from './BankInfoCard';
import infoStyle from './bankinfocard.module.less';
import BankCardSticker from './components/BankCardSticker';
import BankCardTag from './components/BankCardTag';
import { getHashNum, numPrimaryColor } from './map';

export enum Useage {
  EddaList,
}

interface BankInfoCardProps {
  data: EddaBankInfo;
  tmpData: EddaBankInfo;
  style?: CSSProperties;
  status?: BankInfoCardStatus;
  onSetDefault?: (data: EddaBankInfo, type: ProductType) => void;
  onUnbind?: (data: EddaBankInfo) => void;
  onCheck?: (data: EddaBankInfo) => void;
  usage?: Useage; // 该卡片用在什么位置，有相应的不同展示
  checked?: boolean; // 使用选择状态时，是否为选中状态
  appliedProd: ProductType[];
}

type BankCardStickersProps = {
  list: { productCode: ProductType }[];
  appliedProd: ProductType[];
};

function BankCardStickers(props: BankCardStickersProps) {
  const { list, appliedProd } = props;
  const showList = useMemo(
    () =>
      list
        .filter((a) => appliedProd.includes(a.productCode))
        .sort((a, b) => appliedProd.indexOf(a.productCode) - appliedProd.indexOf(b.productCode)),
    [list.length, appliedProd]
  );
  return (
    <div className={infoStyle.stickers}>
      {showList.map((pt) => (
        <BankCardSticker key={pt.productCode} productType={pt.productCode} />
      ))}
    </div>
  );
}

function BankInfoCard(props: BankInfoCardProps) {
  const {
    tmpData,
    data,
    style,
    status = BankInfoCardStatus.DEFAULT,
    onUnbind,
    onCheck,
    usage,
    checked,
    onSetDefault,
    appliedProd,
  } = props;

  const productionHintMap = useMemo(
    () =>
      new Map([
        [ProductType.KKC, t('edda.set.default_kkc')],
        [ProductType.CL, t('edda.set.default_cl')],
        [ProductType.CMC, t('edda.set.default_cmc')],
      ]),
    []
  );

  const {
    bankCardName,
    bankCardNumShow,
    bankCardEnName,
    state = BankEddState.BindSuccess,
    bankCardCode = '',
    productList = [],
  } = status === BankInfoCardStatus.DEFAULT ? data : tmpData;
  const { productList: originProductionList = [] } = data;
  const { productList: tmpProductionList = [] } = tmpData || {};
  const layoutRef = useRef<HTMLDivElement>(null);
  const [tag, setTag] = useState<BankCardTagType>();

  const styleIndex = getHashNum(bankCardCode);

  useEffect(() => {
    switch (true) {
      // case originProductionList.length !== 0 || tmpProductionList.length !== 0:
      // 当为其中一个产品的卡片时，不论更改前后，均不能解除。
      // setTag(BankCardTagType.DEFAULT);
      // break;
      case state === BankEddState.Unbinding:
        setTag(BankCardTagType.UNBINGDING);
        break;
      case state === BankEddState.BindHandling:
        setTag(BankCardTagType.HANDLING);
        break;
      default:
        setTag(BankCardTagType.NotDefault);
        break;
    }
  }, [state, originProductionList, tmpProductionList]);

  const isOccupied = useMemo(
    () => originProductionList.length !== 0 || tmpProductionList.length !== 0,
    [originProductionList, tmpProductionList]
  );

  // 判断是否有右边标签
  const hasRightLabels = useMemo(() => {
    if (status === BankInfoCardStatus.SELECT) return true; // 选择状态有复选框
    if ([BankInfoCardStatus.DEFAULT, BankInfoCardStatus.MANAGEMENT].includes(status)) {
      return productList.length > 0; // 只有当有产品标签时才认为有右边标签
    }
    return false;
  }, [status, productList.length]);

  useEffect(() => {
    layoutRef.current?.style.setProperty('--card_primarycolor', numPrimaryColor[styleIndex]);
    layoutRef.current?.style.setProperty(
      '--card_border_primarycolor',
      `${numPrimaryColor[styleIndex]}40`
    );
  }, [bankCardEnName]);

  const onCheckChange = (checkVal: boolean) => {
    if (checkVal && onCheck) {
      onCheck(data);
    }
  };

  const onClickUnbind = () => {
    onUnbind && onUnbind(data);
  };

  return (
    <div ref={layoutRef} className={infoStyle.outerLayout}>
      <div
        className={infoStyle.layout}
        style={{
          ...style,
        }}
      >
        <div className={infoStyle.bg_text}>{bankCardEnName}</div>
        <div
          className={classNames({
            [infoStyle.bankname_wrapper]: true,
            [infoStyle.bankname_wrapper_between]: status === BankInfoCardStatus.SELECT,
          })}
        >
          <div className={classNames(
            handleEnClsName(infoStyle.bankname),
            hasRightLabels ? infoStyle.bankname_with_labels : infoStyle.bankname_no_labels
          )}>
            <span>{bankCardName}</span>
          </div>
          {[BankInfoCardStatus.DEFAULT, BankInfoCardStatus.MANAGEMENT].includes(status) ? (
            <>
              <BankCardStickers list={productList} appliedProd={appliedProd} />
              {!productList.length ? (
                <BankCardTag tag={tag} className={infoStyle.bankTag} />
              ) : undefined}
            </>
          ) : undefined}
          {status === BankInfoCardStatus.SELECT ? (
            <VCreditCheckbox iconSize={16} checked={checked} onChange={onCheckChange} />
          ) : undefined}
        </div>
        {productList.length ? <BankCardTag tag={tag} className={infoStyle.CLbankTag} /> : undefined}
        <div className={infoStyle.bank_card_title}>{t('銀行賬戶號碼')}</div>
        <div className={infoStyle.bank_card}>{bankCardNumShow?.replace(/• /g, '･')}</div>
      </div>
      {status === BankInfoCardStatus.MANAGEMENT &&
        ![BankEddState.Unbinding, BankEddState.BindHandling].includes(state) ? (
        <div className={infoStyle.bottomlayout}>
          <div className={infoStyle.checkbox_warpper}>
            {appliedProd.map((tmpPt) => (
              <div
                key={tmpPt}
                className={infoStyle.default_check_wrapper}
                onClick={() => onSetDefault && onSetDefault(tmpData, tmpPt)}
              >
                {productList?.some((pli) => pli.productCode === tmpPt) ? (
                  <CheckOn className={`${infoStyle.ignore_default_check}`} />
                ) : (
                  <div className={infoStyle.hollow_circle} />
                )}
                <span>{productionHintMap.get(tmpPt)}</span>
              </div>
            ))}
          </div>
          {!isOccupied ? (
            <div className={infoStyle.unbindtext} onClick={onClickUnbind}>
              {t('edda.manage.delink')}
            </div>
          ) : (
            <div />
          )}
        </div>
      ) : null}
      {usage === Useage.EddaList && status === BankInfoCardStatus.DEFAULT ? (
        state === BankEddState.BindHandling ? (
          <div className={infoStyle.hint}>{t('edda.bankcard.handling_hint')}</div>
        ) : state === BankEddState.Unbinding ? (
          <div className={infoStyle.hint}>{t('edda.bankcard.unbinding_hint')}</div>
        ) : (
          <div />
        )
      ) : (
        <div />
      )}
    </div>
  );
}

BankInfoCard.defaultProps = {
  style: {},
  status: BankInfoCardStatus.MANAGEMENT,
  usage: Useage.EddaList,
};

export default BankInfoCard;
